// Detects the failure class no exception tracker can see: the main thread
// stopping while the player is looking at the page. The opencv fake-thenable
// bug locked every visionMatch trail this way for days and threw NOTHING —
// remote monitoring was blind to it by construction.
//
// Mechanism: while the page is visible, a heartbeat timestamp is written to
// localStorage every couple of seconds; hiding the page or unloading it
// cleanly marks the record as such. At next boot, records left over from
// earlier sessions are classified — one that just STOPPED while visible
// means a main thread died mid-session (hard freeze, renderer crash, or OOM
// kill of a foreground tab) — and a `clientFreezeSuspected` event is sent
// with the last route and the breadcrumb trail from that session.
//
// Storage is localStorage (a frozen tab is usually KILLED; only a later
// session can send its report) but each record is scoped to a TAB via a
// sessionStorage-persisted id in its key — two simultaneous tabs would
// otherwise overwrite each other's beats and mark each other's dirty
// records clean. A live sibling tab is recognised at boot by its FRESH
// dirty record and left alone; only dirty records that have actually
// stopped beating are reported. Records carry no identifier beyond the
// random per-tab suffix — just timestamps, a path, and crumbs — and reports
// flow through posthog.capture's consent gate like every other event.
import { addBreadcrumb, getBreadcrumbTrail } from './breadcrumbs';
import { capturePlayerEvent } from './outbox';

const KEY_PREFIX = 'mt-heartbeat:';
const TAB_ID_KEY = 'mt-heartbeat-tab';
export const HEARTBEAT_INTERVAL_MS = 2_000;
// A record whose newest beat is younger than this is assumed to belong to a
// LIVE tab (timers may lag the 2s cadence under load — heavy wasm inits have
// produced tens of seconds of jank — so the grace is deliberately generous).
// The id-claim step in startFreezeWatchdog guarantees this rule is safe even
// for this tab's own key: any ambiguous fresh-dirty record is moved out from
// under us by minting a new id, so a fresh-dirty record under ANY key means
// a beating tab.
const LIVE_TAB_GRACE_MS = 60_000;
const STALE_REPORT_MAX_AGE_MS = 48 * 60 * 60 * 1000;

export interface HeartbeatRecord {
  /** Last heartbeat, epoch ms. */
  beat: number;
  /** Path at the last heartbeat. */
  path: string;
  /** True once the page was hidden or unloaded in an orderly way. */
  clean: boolean;
  /** Breadcrumb trail snapshot, refreshed with each beat. */
  crumbs: string[];
}

function parseRecord(raw: string | null): HeartbeatRecord | null {
  try {
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    const record = parsed as Partial<HeartbeatRecord>;
    // Must be a finite epoch new Date(...).toISOString() can serialise
    // (|ms| <= 8.64e15) — storage is untrusted and a garbage beat would
    // throw at report time.
    if (
      typeof record.beat !== 'number' ||
      !Number.isFinite(record.beat) ||
      Math.abs(record.beat) > 8.64e15
    ) {
      return null;
    }
    return {
      beat: record.beat,
      path: typeof record.path === 'string' ? record.path : '',
      clean: record.clean === true,
      crumbs: Array.isArray(record.crumbs)
        ? record.crumbs.filter((c): c is string => typeof c === 'string')
        : [],
    };
  } catch {
    return null;
  }
}

const mintId = (): string => Math.random().toString(36).slice(2, 10);

/**
 * This tab's stable id: sessionStorage survives reloads within the tab.
 * NOT unconditionally trusted at boot — "Duplicate tab" COPIES
 * sessionStorage, so an inherited id may still belong to a live parent tab
 * (see the claim logic in startFreezeWatchdog).
 */
function tabId(): string {
  try {
    const existing = window.sessionStorage.getItem(TAB_ID_KEY);
    if (existing) return existing;
    const fresh = mintId();
    window.sessionStorage.setItem(TAB_ID_KEY, fresh);
    return fresh;
  } catch {
    // No sessionStorage → per-load id; records still can't cross tabs.
    return mintId();
  }
}

function write(key: string, record: HeartbeatRecord): void {
  try {
    window.localStorage.setItem(key, JSON.stringify(record));
  } catch {
    // Storage full/unavailable — the watchdog silently stands down.
  }
}

export type RecordVerdict = 'freeze' | 'live-tab' | 'ignore';

/**
 * Pure classification, exported for tests. Fresh-dirty records are LIVE tabs
 * — including this tab's own current record when the deferred re-sweep runs;
 * the id-claim step guarantees a crashed predecessor's record is never
 * sitting fresh under this tab's key when a sweep happens (it either went
 * stale, or a new id was minted and it is judged like any other tab's).
 */
export function classifyRecord(
  record: HeartbeatRecord | null,
  now: number,
): RecordVerdict {
  if (!record) return 'ignore';
  if (record.clean) return 'ignore';
  // Future-dated beats (a clock set backwards, corrupt storage) would
  // otherwise classify as live-tab forever — an immortal record the sweep
  // could never remove. Drop them; a genuinely live tab rewrites its record
  // within a couple of seconds anyway.
  if (record.beat > now) return 'ignore';
  if (now - record.beat > STALE_REPORT_MAX_AGE_MS) return 'ignore';
  if (now - record.beat < LIVE_TAB_GRACE_MS) return 'live-tab';
  return 'freeze';
}

/**
 * Report previous dirty-ended sessions, then start beating for this one.
 * Call once at boot, after posthog.init.
 */
let started = false;
export function startFreezeWatchdog(): void {
  if (started || typeof window === 'undefined') return;
  started = true;

  // Claim an id. If the inherited id's record is dirty AND fresh, its writer
  // may still be ALIVE: "Duplicate tab" copies sessionStorage, so this could
  // be a live parent tab — or equally this tab's own crashed-and-quickly-
  // reopened predecessor. The two are indistinguishable from storage, so
  // mint a fresh id and leave the record alone: a live parent keeps beating
  // (never reported); a crashed predecessor's record goes stale and the
  // deferred re-sweep below reports it as an ordinary other-tab freeze.
  let id = tabId();
  try {
    const inherited = parseRecord(
      window.localStorage.getItem(`${KEY_PREFIX}${id}`)
    );
    if (
      inherited &&
      !inherited.clean &&
      Date.now() - inherited.beat < LIVE_TAB_GRACE_MS
    ) {
      id = mintId();
      try {
        window.sessionStorage.setItem(TAB_ID_KEY, id);
      } catch {
        // Keep the minted id for this load even if it can't persist.
      }
    }
  } catch {
    // Storage unavailable — proceed with whatever id we have.
  }
  const myKey = `${KEY_PREFIX}${id}`;

  const sweep = () => {
    const now = Date.now();
    try {
      for (const key of Object.keys(window.localStorage)) {
        if (!key.startsWith(KEY_PREFIX)) continue;
        const record = parseRecord(window.localStorage.getItem(key));
        const verdict = classifyRecord(record, now);
        if (verdict === 'live-tab') continue;
        if (verdict === 'freeze' && record) {
          capturePlayerEvent('clientFreezeSuspected', {
            lastPath: record.path,
            lastBeatAt: new Date(record.beat).toISOString(),
            breadcrumbs: record.crumbs,
          });
          addBreadcrumb('freeze-reported', record.path);
        }
        // Reported, clean, malformed, or expired — all done with.
        window.localStorage.removeItem(key);
      }
    } catch {
      // Storage unavailable — nothing to report, nothing to beat into.
    }
  };

  sweep();
  // Second pass once the liveness grace has elapsed: catches the ambiguous
  // fresh-dirty record left in place by the id claim above (a crashed
  // predecessor is stale by now; a live parent has kept beating).
  window.setTimeout(sweep, LIVE_TAB_GRACE_MS + HEARTBEAT_INTERVAL_MS);

  const beat = () => {
    write(myKey, {
      beat: Date.now(),
      path: window.location.pathname,
      clean: false,
      crumbs: getBreadcrumbTrail(),
    });
  };
  const markClean = () => {
    // Scoped to THIS tab's record only — a sibling tab's orderly shutdown
    // must never absolve another tab's dirty record.
    const current = parseRecord(window.localStorage.getItem(myKey));
    if (current) write(myKey, { ...current, clean: true });
  };

  let interval: number | null = null;
  const resume = () => {
    if (interval !== null) return;
    beat();
    interval = window.setInterval(beat, HEARTBEAT_INTERVAL_MS);
  };
  const pause = () => {
    if (interval !== null) {
      window.clearInterval(interval);
      interval = null;
    }
    markClean();
  };

  // Beat only while visible: a backgrounded tab's throttled (or frozen —
  // that's normal platform behaviour) timers must not look like a crash, so
  // hiding counts as an orderly end and re-showing starts a fresh dirty run.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      resume();
    } else {
      pause();
    }
  });
  // pagehide is the reliable end-of-life signal on mobile (beforeunload is
  // not); it also fires before bfcache suspension, which pairs with the
  // visibilitychange resume on restore.
  window.addEventListener('pagehide', pause);

  if (document.visibilityState === 'visible') resume();
}
